/**
 * DeploymentHistoryPage.tsx
 * Feature: 040-deployment-history — Deployment History Tracking
 * Author: Peter Dobler
 * Date: 2026-03-28
 * Constitution Version: 1.12.0
 * Modified: 2026-06-15 | Conformed to Spec 197 design blueprint — migrated onto
 *           the canonical Tailwind+token UI kit (PageLayout/Card/DataTable/Badge/
 *           Button/Select/Input/states). Presentation-only; server pagination and
 *           filter behavior unchanged.
 *
 * Purpose: Read-only paginated table of all infrastructure deployment records.
 *          Supports server-side filtering by environment, component, and date
 *          range. No create or delete actions are exposed (FR-010, FR-013).
 */

import React, { useState } from 'react';
import { ChevronLeft, ChevronRight, History } from 'lucide-react';
import { useDeploymentHistory } from '../../hooks/useDeploymentHistory';
import {
  PageLayout,
  Card,
  DataTable,
  Button,
  Input,
  Select,
  Badge,
  EmptyState,
  ErrorState,
  LoadingState,
} from '../../components/ui';
import type { DataTableColumn } from '../../components/ui';
import type {
  DeploymentEnvironment,
  DeploymentComponent,
  DeploymentHistoryFilters,
  DeploymentRecord,
} from '@shared/types/deploymentHistory';
import { formatEastern } from '@/utils/dateFormatter';

// =============================================================================
// Badge helpers
// =============================================================================

function EnvironmentBadge({ env }: { env: DeploymentEnvironment }) {
  return (
    <Badge variant={env === 'prod' ? 'warning' : 'info'} isOutlined>
      {env}
    </Badge>
  );
}

function ComponentBadge({ component }: { component: DeploymentComponent }) {
  const variant: Record<DeploymentComponent, React.ComponentProps<typeof Badge>['variant']> = {
    collection: 'primary',
    backup: 'success',
    web: 'info',
    database: 'warning',
    logging: 'secondary',
    brain: 'secondary',
    'collection-worker': 'secondary',
  };
  return (
    <Badge variant={variant[component]} isOutlined>
      {component}
    </Badge>
  );
}

function StatusBadge({ status }: { status: DeploymentRecord['status'] }) {
  return (
    <Badge variant={status === 'success' ? 'success' : 'error'} isOutlined>
      {status}
    </Badge>
  );
}

// =============================================================================
// Helpers
// =============================================================================

function formatDate(iso: string): string {
  try {
    return formatEastern(iso);
  } catch {
    return iso;
  }
}

function formatDuration(seconds: number | null): string {
  if (seconds === null) return '—';
  if (seconds < 60) return `${seconds}s`;
  return `${Math.floor(seconds / 60)}m ${seconds % 60}s`;
}

function truncateSha(sha: string): string {
  return sha === 'unknown' ? sha : sha.slice(0, 8);
}

// =============================================================================
// Main Page
// =============================================================================

const DEFAULT_PAGE_SIZE = 50;

export default function DeploymentHistoryPage() {
  const [filters, setFilters] = useState<DeploymentHistoryFilters>({
    page: 1,
    pageSize: DEFAULT_PAGE_SIZE,
  });

  const { data, isLoading, isError, error, refetch, isFetching } = useDeploymentHistory(filters);

  function handleFilterChange(newFilters: Omit<DeploymentHistoryFilters, 'page' | 'pageSize'>) {
    // When newFilters is empty (clear action), reset to defaults
    if (Object.keys(newFilters).length === 0) {
      setFilters((prev) => ({ page: 1, pageSize: prev.pageSize ?? DEFAULT_PAGE_SIZE }));
    } else {
      setFilters((prev) => ({ ...prev, ...newFilters, page: 1 }));
    }
  }

  function handleFilterField(
    key: keyof Omit<DeploymentHistoryFilters, 'page' | 'pageSize'>,
    value: string,
  ) {
    handleFilterChange({
      environment: filters.environment,
      component: filters.component,
      from: filters.from,
      to: filters.to,
      [key]: value || undefined,
    });
  }

  function handlePageChange(page: number) {
    setFilters((prev) => ({ ...prev, page }));
  }

  const hasActiveFilters =
    !!filters.environment || !!filters.component || !!filters.from || !!filters.to;

  const totalPages = data ? Math.max(1, Math.ceil(data.total / (filters.pageSize ?? DEFAULT_PAGE_SIZE))) : 1;
  const currentPage = filters.page ?? 1;

  const columns: DataTableColumn<DeploymentRecord>[] = [
    {
      key: 'component',
      header: 'Component',
      width: '11%',
      render: (r) => <ComponentBadge component={r.component} />,
    },
    {
      key: 'environment',
      header: 'Environment',
      width: '11%',
      render: (r) => <EnvironmentBadge env={r.environment} />,
    },
    {
      key: 'imageTag',
      header: 'Image Tag',
      width: '20%',
      render: (r) => (
        <span title={r.imageTag} className="font-mono text-xs text-[var(--color-text-primary)]">
          {r.imageTag}
        </span>
      ),
    },
    {
      key: 'gitSha',
      header: 'Git SHA',
      width: '11%',
      render: (r) => (
        <span
          title={r.gitSha}
          className="rounded bg-[var(--color-bg-subtle)] px-1.5 py-0.5 font-mono text-xs text-[var(--color-text-primary)]"
        >
          {truncateSha(r.gitSha)}
        </span>
      ),
    },
    {
      key: 'deployedBy',
      header: 'Deployed By',
      width: '13%',
      render: (r) => <span className="text-[var(--color-text-secondary)]">{r.deployedBy}</span>,
    },
    {
      key: 'deployedAt',
      header: 'Deployed At',
      width: '15%',
      render: (r) => <span className="text-[var(--color-text-secondary)]">{formatDate(r.deployedAt)}</span>,
    },
    {
      key: 'status',
      header: 'Status',
      width: '10%',
      render: (r) => <StatusBadge status={r.status} />,
    },
    {
      key: 'durationSeconds',
      header: 'Duration',
      width: '9%',
      align: 'right',
      render: (r) => (
        <span className="text-[var(--color-text-secondary)]">{formatDuration(r.durationSeconds)}</span>
      ),
    },
  ];

  return (
    <PageLayout
      title="Deployment History"
      icon={<History className="h-6 w-6" />}
      helpId="internal.deployment-history"
      onRefresh={async () => { await refetch(); }}
      isRefreshing={isFetching}
      description={
        <>
          Infrastructure deployment audit trail — read-only. Records are written automatically by{' '}
          <code className="rounded bg-[var(--color-bg-subtle)] px-1 font-mono text-xs">deploy.ps1</code>.
        </>
      }
    >
      {/* Filters */}
      <Card className="mb-4 p-4">
        <div className="flex flex-wrap items-end gap-3">
          <div className="w-40">
            <Select
              isFullWidth
              label="Environment"
              value={filters.environment ?? ''}
              onChange={(e) => handleFilterField('environment', e.target.value)}
              options={[
                { value: '', label: 'All' },
                { value: 'dev', label: 'dev' },
                { value: 'prod', label: 'prod' },
              ]}
            />
          </div>
          <div className="w-44">
            <Select
              isFullWidth
              label="Component"
              value={filters.component ?? ''}
              onChange={(e) => handleFilterField('component', e.target.value)}
              options={[
                { value: '', label: 'All' },
                { value: 'collection', label: 'collection' },
                { value: 'backup', label: 'backup' },
                { value: 'web', label: 'web' },
                { value: 'database', label: 'database' },
                { value: 'logging', label: 'logging' },
                { value: 'brain', label: 'brain' },
                { value: 'collection-worker', label: 'collection-worker' },
              ]}
            />
          </div>
          <div className="w-44">
            <Input
              isFullWidth
              type="date"
              label="From"
              value={filters.from ? filters.from.slice(0, 10) : ''}
              onChange={(e) =>
                handleFilterField('from', e.target.value ? `${e.target.value}T00:00:00Z` : '')
              }
            />
          </div>
          <div className="w-44">
            <Input
              isFullWidth
              type="date"
              label="To"
              value={filters.to ? filters.to.slice(0, 10) : ''}
              onChange={(e) =>
                handleFilterField('to', e.target.value ? `${e.target.value}T23:59:59Z` : '')
              }
            />
          </div>
          {hasActiveFilters && (
            <Button variant="ghost" size="sm" onClick={() => handleFilterChange({})}>
              Clear filters
            </Button>
          )}
        </div>
      </Card>

      {/* States */}
      {isLoading ? (
        <Card className="p-4">
          <LoadingState rows={8} />
        </Card>
      ) : isError ? (
        <ErrorState
          title="Failed to load deployment history"
          message={error instanceof Error ? error.message : 'Unknown error'}
        />
      ) : !data || data.data.length === 0 ? (
        <Card padding="none">
          <EmptyState
            icon={<History className="h-10 w-10" />}
            title="No deployments recorded yet"
            message={
              <>
                Records appear here after each{' '}
                <code className="rounded bg-[var(--color-bg-subtle)] px-1 font-mono text-xs">deploy.ps1</code>{' '}
                run.
              </>
            }
          />
        </Card>
      ) : (
        <>
          <Card padding="none">
            {/* pageSize > server page so DataTable shows the whole server page; the
                server pagination controls below own page navigation. */}
            <DataTable
              columns={columns}
              rows={data.data}
              rowKey={(r) => r.id}
              pageSize={1000}
              minWidth="52rem"
              emptyMessage="No deployments recorded yet"
            />
          </Card>

          {/* Server pagination */}
          <div className="flex items-center justify-between pt-3">
            <p className="text-xs text-[var(--color-text-secondary)]">
              Showing {((currentPage - 1) * (filters.pageSize ?? DEFAULT_PAGE_SIZE)) + 1}–
              {Math.min(currentPage * (filters.pageSize ?? DEFAULT_PAGE_SIZE), data.total)} of {data.total} records
            </p>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                leftIcon={<ChevronLeft className="h-4 w-4" />}
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage <= 1}
              >
                Prev
              </Button>
              <span className="text-xs text-[var(--color-text-secondary)]">
                Page {currentPage} of {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                rightIcon={<ChevronRight className="h-4 w-4" />}
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage >= totalPages}
              >
                Next
              </Button>
            </div>
          </div>
        </>
      )}
    </PageLayout>
  );
}
